import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Download, ExternalLink, BarChart3, DollarSign, Building2, FileText, Eye, Printer,
} from 'lucide-react';

import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * B2BOrdersTab — Dashboard tab listing B2B pass orders + invoices + commissions.
 * Visible to both B2B customers (their own orders) and managers (all + commission overview).
 *
 * Extracted from App.js (Fase 2) — pure relocation, no logic changes.
 */
export const B2BOrdersTab = ({ user, isManager = false }) => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOrder, setViewOrder] = useState(null);
  const [commissionData, setCommissionData] = useState(null);

  useEffect(() => {
    fetchMyOrders();
    if (isManager) fetchCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMyOrders = async () => {
    try {
      const response = await axios.get(`${API}/b2b/my-orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch B2B orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissions = async () => {
    try {
      const response = await axios.get(`${API}/b2b/my-commissions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
      });
      setCommissionData(response.data);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      invoiced: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</Badge>;
  };

  const handlePrintInvoice = (order) => {
    const invoiceHTML = `
      <!DOCTYPE html>
      <html><head><title>Invoice ${order.invoice_number || 'Draft'}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
        .logo { font-size: 24px; font-weight: bold; color: #1a73e8; }
        .invoice-title { font-size: 32px; color: #666; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .info-section h3 { color: #1a73e8; border-bottom: 2px solid #1a73e8; padding-bottom: 5px; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
        th { background: #f5f5f5; }
        .total-row { font-weight: bold; font-size: 18px; background: #e8f0fe; }
        .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
      </style></head><body>
        <div class="header"><div class="logo">Freestays</div><div class="invoice-title">INVOICE</div></div>
        <div class="info-grid">
          <div class="info-section"><h3>Bill To</h3>
            <p><strong>${order.business_name}</strong></p>
            <p>${order.business_address || ''}</p>
            <p>${order.business_email}</p>
            <p>${order.business_phone || ''}</p>
            <p>VAT: ${order.vat_number || ''}</p>
          </div>
          <div class="info-section"><h3>Invoice Details</h3>
            <p><strong>Invoice #:</strong> ${order.invoice_number || 'N/A'}</p>
            <p><strong>Order ID:</strong> ${order.order_id}</p>
            <p><strong>Date:</strong> ${new Date(order.created_at).toLocaleDateString()}</p>
            <p><strong>Status:</strong> ${order.status?.toUpperCase()}</p>
          </div>
        </div>
        <table>
          <thead><tr><th>Description</th><th>Type</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
          <tbody>
            <tr>
              <td>Freestays Pass</td>
              <td>${order.pass_type === 'annual' ? 'Annual Pass' : order.pass_type === 'free_annual' ? 'Free Annual Pass' : 'One-Time Pass'}</td>
              <td>${order.quantity}</td>
              <td>€${order.unit_price?.toFixed(2)}</td>
              <td>€${order.total_price?.toFixed(2)}</td>
            </tr>
            <tr class="total-row">
              <td colspan="4" style="text-align: right;">Total (excl. VAT)</td>
              <td>€${order.total_price?.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        ${order.pass_codes?.length ? `<div class="info-section"><h3>Pass Codes</h3><p>${order.pass_codes.join(', ')}</p></div>` : ''}
        <div class="footer"><p>Freestays - Waanderweg 50a, Emmen</p><p>www.freestays.eu | administration@freestays.eu</p></div>
      </body></html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const handleExportCSV = () => {
    const headers = ['Business', 'Email', 'Pass Type', 'Qty', 'Unit Price', 'Total', 'Invoice', 'Status', 'Created'];
    const rows = orders.map(o => [
      o.business_name, o.business_email, o.pass_type, o.quantity,
      o.unit_price, o.total_price, o.invoice_number || '', o.status,
      new Date(o.created_at).toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my_b2b_sales_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const totalRevenue = orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + (o.total_price || 0), 0);
  const totalPasses = orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + (o.quantity || 0), 0);
  const pendingCount = orders.filter(o => o.status === 'pending' || o.status === 'invoiced').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-2xl">{isManager ? 'My Sales B2B' : 'My B2B Passes'}</h2>
          <p className="text-muted-foreground">
            {isManager
              ? t('dashboard.b2bManagerSubtitle', 'Manage B2B pass orders, invoices and enter new sales')
              : t('dashboard.b2bSubtitle', 'View your B2B pass orders and invoices')}
          </p>
        </div>
        <div className="flex gap-2">
          {orders.length > 0 && (
            <Button variant="outline" className="rounded-full" onClick={handleExportCSV} data-testid="b2b-export-csv">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          {isManager && (
            <Button variant="outline" className="rounded-full" onClick={() => window.open('https://www.freestays.eu/cms/b2b', '_blank')} data-testid="b2b-open-cms">
              <ExternalLink className="w-4 h-4 mr-2" />
              CMS B2B Orders
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {orders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 rounded-2xl">
            <p className="text-sm text-muted-foreground">Total Orders</p>
            <p className="text-2xl font-bold">{orders.length}</p>
          </Card>
          <Card className="p-4 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-600 dark:text-yellow-400">Pending</p>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{pendingCount}</p>
          </Card>
          <Card className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <p className="text-sm text-green-600 dark:text-green-400">{isManager ? 'Revenue' : 'Total Spent'}</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">€{totalRevenue.toFixed(2)}</p>
          </Card>
          <Card className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-600 dark:text-blue-400">{isManager ? 'Passes Sold' : 'Passes Purchased'}</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalPasses}</p>
          </Card>
        </div>
      )}

      {/* Pass Distribution - B2B customers */}
      {!isManager && orders.length > 0 && (() => {
        const paidOrders = orders.filter(o => o.status === 'paid');
        const annualCount = paidOrders.filter(o => o.pass_type === 'annual' || o.pass_type === 'free_annual').reduce((s, o) => s + (o.quantity || 0), 0);
        const oneTimeCount = paidOrders.filter(o => o.pass_type === 'one_time').reduce((s, o) => s + (o.quantity || 0), 0);
        const totalCount = annualCount + oneTimeCount;
        return (
          <Card className="p-6 rounded-2xl">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Pass Distribution
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">Annual Pass</span>
                  <span className="text-sm font-medium">{annualCount} ({totalCount > 0 ? Math.round(annualCount / totalCount * 100) : 0}%)</span>
                </div>
                <div className="w-full bg-secondary rounded-full h-3">
                  <div className="bg-[#00A9E0] h-3 rounded-full transition-all" style={{ width: `${totalCount > 0 ? (annualCount / totalCount * 100) : 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">One-Time Pass</span>
                  <span className="text-sm font-medium">{oneTimeCount} ({totalCount > 0 ? Math.round(oneTimeCount / totalCount * 100) : 0}%)</span>
                </div>
                <div className="w-full bg-secondary rounded-full h-3">
                  <div className="bg-amber-500 h-3 rounded-full transition-all" style={{ width: `${totalCount > 0 ? (oneTimeCount / totalCount * 100) : 0}%` }} />
                </div>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Commission Overview - Managers only */}
      {isManager && commissionData && (
        <Card className="p-6 rounded-2xl">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            Commission Overview
          </h3>

          {/* Commission Totals */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl text-center">
              <p className="text-xs text-green-600 dark:text-green-400">Netto Commissie</p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">€{commissionData.totals?.total_netto?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-900/20 rounded-xl text-center">
              <p className="text-xs text-slate-600 dark:text-slate-400">BTW (21%)</p>
              <p className="text-xl font-bold text-slate-600 dark:text-slate-400">€{commissionData.totals?.total_btw?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-center">
              <p className="text-xs text-amber-600 dark:text-amber-400">Pending</p>
              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">€{commissionData.totals?.total_pending?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
              <p className="text-xs text-blue-600 dark:text-blue-400">Uitbetaald</p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">€{commissionData.totals?.total_paid_out?.toFixed(2) || '0.00'}</p>
            </div>
          </div>

          {/* Commission Rates Info */}
          <div className="flex gap-4 mb-4 p-3 bg-secondary/30 rounded-lg text-sm text-muted-foreground">
            <span>Commissie: <strong className="text-foreground">20%</strong> over netto verkoopprijs (excl. BTW)</span>
          </div>

          {/* Monthly Breakdown */}
          {commissionData.monthly?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-muted-foreground font-medium">Maand</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">Vouchers</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">Passes</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">Netto</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">BTW</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">Totaal</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionData.monthly.map(m => (
                    <tr key={m.month} className="border-b border-border/50">
                      <td className="p-3 font-medium">{m.month}</td>
                      <td className="p-3">{m.vouchers}</td>
                      <td className="p-3">{m.passes}</td>
                      <td className="p-3">€{(m.total_netto || 0).toFixed(2)}</td>
                      <td className="p-3 text-muted-foreground">€{(m.total_btw || 0).toFixed(2)}</td>
                      <td className="p-3 font-medium text-green-500">€{m.total.toFixed(2)}</td>
                      <td className="p-3">
                        {m.pending > 0 ? (
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">€{m.pending.toFixed(2)} pending</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Paid out</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!commissionData.monthly || commissionData.monthly.length === 0) && (
            <p className="text-center text-muted-foreground py-4">No commissions yet. Commissions are generated when B2B orders are marked as paid.</p>
          )}
        </Card>
      )}

      {/* Orders Table */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <Card key={i} className="p-6">
              <div className="h-20 skeleton rounded" />
            </Card>
          ))}
        </div>
      ) : orders.length > 0 ? (
        <Card className="rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="b2b-sales-table">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Business</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Pass Type</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Qty</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Total</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Invoice</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Created</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.order_id} className="border-b border-border/50 hover:bg-secondary/20 transition-colors" data-testid={`b2b-order-${order.order_id}`}>
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{order.business_name}</p>
                        <p className="text-xs text-muted-foreground">{order.business_email}</p>
                      </div>
                    </td>
                    <td className="p-4 text-sm">{order.pass_type === 'annual' ? 'Annual' : order.pass_type === 'free_annual' ? 'Free Annual' : 'One-Time'}</td>
                    <td className="p-4 text-sm">{order.quantity}</td>
                    <td className="p-4 text-sm font-medium text-green-500">€{order.total_price?.toFixed(2)}</td>
                    <td className="p-4 text-sm text-muted-foreground">{order.invoice_number || '-'}</td>
                    <td className="p-4">{getStatusBadge(order.status)}</td>
                    <td className="p-4 text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setViewOrder(order)} title="View Details" data-testid={`view-order-${order.order_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {order.invoice_number && (
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handlePrintInvoice(order)} title="Print Invoice" data-testid={`print-order-${order.order_id}`}>
                            <Printer className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="p-16 text-center rounded-3xl border-0 shadow-lg bg-gradient-to-br from-secondary/50 to-background">
          <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-12 h-12 text-primary/30" />
          </div>
          <h3 className="font-bold text-2xl mb-3">{isManager ? 'No B2B Orders Yet' : 'No Passes Yet'}</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            {isManager
              ? 'Start selling B2B passes via the "Create Order" tab or the CMS.'
              : 'Your purchased B2B passes will appear here once they have been processed.'}
          </p>
          {isManager && (
            <Button variant="outline" className="rounded-full px-8 h-12" onClick={() => window.open('https://www.freestays.eu/cms/b2b', '_blank')}>
              <ExternalLink className="w-5 h-5 mr-2" />
              Open CMS
            </Button>
          )}
        </Card>
      )}

      {/* View Order Detail Dialog */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              B2B Order Details
            </DialogTitle>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Business Name</p>
                  <p className="font-medium">{viewOrder.business_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">VAT Number</p>
                  <p className="font-medium">{viewOrder.vat_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{viewOrder.business_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{viewOrder.business_phone || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{viewOrder.business_address || '-'}</p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Pass Type</p>
                  <p className="font-medium">{viewOrder.pass_type === 'annual' ? 'Annual' : viewOrder.pass_type === 'free_annual' ? 'Free Annual' : 'One-Time'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{viewOrder.quantity}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Unit Price</p>
                  <p className="font-medium">€{viewOrder.unit_price?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="font-medium text-green-500">€{viewOrder.total_price?.toFixed(2)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(viewOrder.status)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Invoice</p>
                  <p className="font-medium text-blue-500">{viewOrder.invoice_number || 'Not yet generated'}</p>
                </div>
              </div>
              {viewOrder.pass_codes && viewOrder.pass_codes.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Pass Codes</p>
                    <div className="flex flex-wrap gap-2">
                      {viewOrder.pass_codes.map((code, i) => (
                        <Badge key={i} variant="outline" className="font-mono">{code}</Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {viewOrder.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p className="text-sm">{viewOrder.notes}</p>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-4">
                {viewOrder.invoice_number && (
                  <Button variant="outline" onClick={() => handlePrintInvoice(viewOrder)}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print Invoice
                  </Button>
                )}
                <Button variant="outline" onClick={() => setViewOrder(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default B2BOrdersTab;
